import * as React from "react";
import {EquipmentDataDropDownResult, KatalMetricsHelperContext} from "../..";
import {
    Delivery,
    GetDeliveriesDetailsGQLResponse
} from "../common/GetDeliveriesDetailsGQLResponse";
import {getDeliveryDetails} from "../../../gql/queries/lmdp/get_delivery_details";
import {useArgoContext} from "@awsscm/argo-core";
import {
    Alert,
    Box,
    BreadcrumbGroup,
    ContentLayout,
    Header,
    SpaceBetween
} from "@amzn/awsui-components-react-v3";
import {
  CABLING_MANIFEST_TABLE_CONSTANT, FORBIDDEN_MESSAGE,
  PLANNED_DELIVERIES_PAGE,
  SEARCH_FILTER
} from "../LmdpConstants";
import ClusterAndSiteSearchFilter from "../common/LmdpClusterAndSiteSearchFilter";
import {convertMultiSelectOptionsToPolaris3Schema} from "../../../util/PolarisUtils";
import {getClusterNodesWithId, getDCNodes, getSuperAzNodes} from "../../../util/EquipmentUtil";
import {useContext, useEffect, useState} from "react";
import {AggregateCablingManifestBySite} from "../plannedDeliveries/interface/AggregateCablingManifestBySite";
import {getAggregateCablingStatusForWeeks} from "../mappers/AggregateCablingStatusBySite";
import CablingManifestRawDataTable from "./CablingManifestRawDataTable";
import {SearchFilteredData} from "../common/LmdpClusterAndSiteSerachFilterData";
import CablingStatusTab from "./CablingStatusTab";
import {
    getCurrentDateInUTCEpochSeconds,
    getThirdSaturdayFromToday
} from "../../../util/DateUtil";
import {getSortedEquipmentDataDropDownInput} from "../common/LmdpUtil";
import {processGQLQuery} from "../../../util/GraphQLQueryHelper";
import getApolloClient from "../../../gql";
import {METRICS_CONSTANT} from "../../metrics/MetricsContants";
import CablingManifestRackGroupChart from "./CablingManifestRackGroupChart";
import {isUserAccessDenied, parseException} from "../../../util/ExceptionUtil";
import {ERROR_MESSAGE} from "../../../util/ConfigConstants";
import {getForbiddenMessageWithTemplateLink} from "../../../util/ReactUtil";

interface CablingForecastPageProps {
    equipmentDataDropDownResult: EquipmentDataDropDownResult,
}

interface SearchConfig {
    selectedFilters?: SearchFilteredData,
    clickCount: number
}

export default function CablingManifestPage(props: CablingForecastPageProps) {

    const context = useArgoContext();
    const katalMetricsHelper = useContext(KatalMetricsHelperContext);

    const [searchResultsLoading, setSearchResultsLoading] = React.useState<boolean>(false);
    const [searchConfig, setSearchConfig] = React.useState<SearchConfig>({clickCount: 0});
    const [showResultsTable, setShowResultsTable] = React.useState<boolean>(false);
    const [deliveriesList, setDeliveriesList] = React.useState<Delivery[]>([]);
    const [searchError, setSearchError] = React.useState<boolean>(false);
    const [siteListSelected, setSiteListSelected] = useState<string[]>([]);
    const [aggregatedCablingStatus, setAggregatedCablingStatus] = useState<AggregateCablingManifestBySite[]>([]);
    const [errorMessage, setErrorMessage] = React.useState<React.ReactElement | undefined>(undefined);
    const [errorHeader, setErrorHeader] = React.useState<string | undefined>(undefined);

  function DoPerformSearch(selectedFilters: SearchFilteredData): any {
        let selectedSiteList: string[] = [];
        selectedFilters.destinationSite.forEach((value) => {
            if (value.label !== undefined)
                selectedSiteList.push(value.label)
        });
        setSiteListSelected(selectedSiteList);
        setSearchConfig({clickCount: searchConfig.clickCount + 1, selectedFilters: selectedFilters});
    }

    useEffect(() => {
        if (searchConfig.clickCount === 0) {
            return;
        }
        if (searchConfig.selectedFilters === undefined) {
            return;
        }

        // Todo add this method in common place
        const queryVariable = {
            variables: {
                "getDeliveryDetailsRequest": {
                    "siteList": siteListSelected,
                    "superAzList": searchConfig.selectedFilters?.superAz?.map(superAz => superAz.value),
                    "clusterList": searchConfig.selectedFilters?.cluster?.map(cluster => cluster.label),
                    "deliveryPlan": {
                        "deliveryDate": {
                            "endDate": getThirdSaturdayFromToday(),
                            "startDate": getCurrentDateInUTCEpochSeconds()
                        }
                    },
                    "propertyFilterList": [{
                        "propertyFilterName": "RATS_CONFIRMATION",
                        "propertyFilterType": {
                            "isOneOf": "CONFIRMED"
                        }
                    }]
                }
            }
        }
        setShowResultsTable(true);
        setSearchResultsLoading(true);
        setSearchError(false);
        processGQLQuery<GetDeliveriesDetailsGQLResponse>(getApolloClient(context), getDeliveryDetails, queryVariable.variables, katalMetricsHelper, METRICS_CONSTANT.GQL_API_NAME.GET_DELIVERY_DETAILS, METRICS_CONSTANT.METHOD_NAME.CABLING_MANIFEST)
            .then((data: GetDeliveriesDetailsGQLResponse) => {
                setSearchResultsLoading(false);
                if (data?.ScosTransportationLMDP_getDeliveryDetails?.deliveries) {
                    setSearchError(false);
                    setDeliveriesList(data.ScosTransportationLMDP_getDeliveryDetails.deliveries);
                    setAggregatedCablingStatus(getAggregateCablingStatusForWeeks(data.ScosTransportationLMDP_getDeliveryDetails.deliveries, 3));
                } else {
                    setSearchError(true);
                }
            })
            .catch((error) => {
                setSearchResultsLoading(false);
                setShowResultsTable(false);
                if (isUserAccessDenied(error)) {
                  setErrorMessage(getForbiddenMessageWithTemplateLink(FORBIDDEN_MESSAGE, false));
                  setErrorHeader(ERROR_MESSAGE.GENERIC_ACCESS_DENIED_ERROR_MESSAGE);
                } else {
                  setErrorMessage(<>{PLANNED_DELIVERIES_PAGE.SEARCH.ERROR_MESSAGE_DESCRIPTION}</>);
                  setErrorHeader(PLANNED_DELIVERIES_PAGE.SEARCH.GENERIC_INTERNAL_SERVER_ERROR_MESSAGE);
                }
                katalMetricsHelper.logError(`UnKnown Error encountered for getDeliveryDetails API with Error ${error}`);
                katalMetricsHelper.emitCountMetrics("Error:" + METRICS_CONSTANT.GQL_API_NAME.GET_DELIVERY_DETAILS,
                    METRICS_CONSTANT.METHOD_NAME.CABLING_MANIFEST, 1);
                setSearchError(true);
            });
    }, [searchConfig]); // eslint-disable-line react-hooks/exhaustive-deps

    function resetSearchResults() {
        setShowResultsTable(false);
        setSearchResultsLoading(false);
        setSearchError(false);
    }

    return (
        <ContentLayout header={
            <>
                <Box className="awsui-app-layout__breadcrumbs" awsui-app-layout-region="breadcrumbs">
                    <BreadcrumbGroup items={CABLING_MANIFEST_TABLE_CONSTANT.BREADCRUMB_ITEMS}/>
                </Box>
                <Box>
                    <Header variant="h1">
                        {CABLING_MANIFEST_TABLE_CONSTANT.PAGE_HEADER}
                    </Header>
                </Box>
            </>
        }>
            <SpaceBetween direction='vertical' size='m'>
                <ClusterAndSiteSearchFilter
                    loading={searchResultsLoading}
                    clusterOptions={{
                        loading: props.equipmentDataDropDownResult.loading,
                        isError: props.equipmentDataDropDownResult.error ? true : false,
                        errorDescription: parseException(SEARCH_FILTER.FILTER_CLUSTER_ERROR_TEXT, props.equipmentDataDropDownResult.error),
                        options: convertMultiSelectOptionsToPolaris3Schema(
                            getSortedEquipmentDataDropDownInput(getClusterNodesWithId(props.equipmentDataDropDownResult.data)))
                    }}
                    superAzOptions={{
                        loading: props.equipmentDataDropDownResult.loading,
                        isError: props.equipmentDataDropDownResult.error ? true : false,
                        errorDescription: parseException(SEARCH_FILTER.FILTER_CLUSTER_ERROR_TEXT, props.equipmentDataDropDownResult.error),
                        options: convertMultiSelectOptionsToPolaris3Schema(
                            getSortedEquipmentDataDropDownInput(getSuperAzNodes(props.equipmentDataDropDownResult.data)))
                    }}
                    siteOptions={{
                        loading: props.equipmentDataDropDownResult.loading,
                        isError: props.equipmentDataDropDownResult.error ? true : false,
                        errorDescription: parseException(SEARCH_FILTER.FILTER_SITE_ERROR_TEXT, props.equipmentDataDropDownResult.error),
                        options: convertMultiSelectOptionsToPolaris3Schema(
                            getSortedEquipmentDataDropDownInput(getDCNodes(props.equipmentDataDropDownResult.data)))
                    }}
                    performSearch={DoPerformSearch}
                    resetSearchResults={resetSearchResults}
                />
                {searchError ? <Alert
                    onDismiss={() => setSearchError(false)}
                    visible={searchError}
                    dismissAriaLabel="Close alert"
                    type="error"
                    header={errorHeader}>
                    {errorMessage}
                </Alert>: null}
                {showResultsTable ? <CablingManifestRackGroupChart items={deliveriesList} isLoading={searchResultsLoading}/> : null}
                {showResultsTable ? <CablingStatusTab items={aggregatedCablingStatus} isLoading={searchResultsLoading}/> : null}
                {showResultsTable ? <CablingManifestRawDataTable items={deliveriesList} isLoading={searchResultsLoading}></CablingManifestRawDataTable> : null}
            </SpaceBetween>

        </ContentLayout>
    );
}